// Toolbar menus + the active-filter chip for the data-table-v2 kit.
//
// ColumnsMenu — checkbox toggles for the table's columns (the pinned leading
// column is fixed). FiltersMenu — a single-select preset list. Both portaled
// (AnchoredPopover) so they never clip. ActiveFilterChip — the removable pill
// shown while a filter is applied.
import { AnimatePresence, motion } from 'framer-motion'
import { AppButton, Checkbox, ListItem, MenuList } from '@clickguard/design-system'
import { AnchoredPopover } from './anchored-popover'
import { ChevronDown, Columns03, FilterLines, XClose } from './icons'

export const ColumnsMenu = <K extends string>({
	columns,
	visible,
	onToggle,
	pinnedLabel
}: {
	columns: Array<{ key: K; label: string }>
	visible: Set<K>
	onToggle: (k: K) => void
	/** The always-on leading column's name (e.g. "Dimension"). */
	pinnedLabel: string
}) => (
	<AnchoredPopover
		align="right"
		width={240}
		renderTrigger={(toggle, open) => (
			<AppButton
				variant="secondary"
				size="sm"
				label="Columns"
				leftIcon={<Columns03 className="h-4 w-4" />}
				rightIcon={<ChevronDown className="h-4 w-4" />}
				aria-haspopup="dialog"
				aria-expanded={open}
				onClick={toggle}
			/>
		)}
	>
		{() => (
			<MenuList role="group">
				{/* The leading column is always on — a disabled (DS-canonical) checked
				    row. No wrapper opacity dimming (that was what made it look too
				    faint before); the checkbox's own disabled state carries it. */}
				<label className="flex cursor-not-allowed items-center gap-2.5 rounded-lg px-2 py-2">
					<Checkbox size="sm" checked disabled onChange={() => undefined} />
					<span className="text-[14px] text-gray-500">{pinnedLabel}</span>
				</label>
				{columns.map((c) => (
					<label
						key={c.key}
						className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 transition-colors hover:bg-[rgba(0,0,0,0.05)]"
					>
						<Checkbox size="sm" checked={visible.has(c.key)} onChange={() => onToggle(c.key)} />
						<span className="text-[14px] text-gray-700">{c.label}</span>
					</label>
				))}
			</MenuList>
		)}
	</AnchoredPopover>
)

export const FiltersMenu = <Id extends string>({
	options,
	value,
	onChange
}: {
	options: Array<{ id: Id; label: string }>
	value: Id
	onChange: (v: Id) => void
}) => (
	<AnchoredPopover
		align="left"
		width={240}
		renderTrigger={(toggle, open) => (
			<AppButton
				variant="secondary"
				size="sm"
				label="Filters"
				leftIcon={<FilterLines className="h-4 w-4" />}
				aria-haspopup="dialog"
				aria-expanded={open}
				onClick={toggle}
			/>
		)}
	>
		{(close) => (
			<MenuList>
				{options.map((f) => (
					<ListItem
						key={f.id}
						label={f.label}
						selected={value === f.id}
						onClick={() => {
							onChange(f.id)
							close()
						}}
					/>
				))}
			</MenuList>
		)}
	</AnchoredPopover>
)

// Active-filter chip — the removable pill shown when a filter is applied (Figma
// node 5345:181113: brand/50 bg, field name in gray-600 + value in ink, dismiss
// X). The WHOLE pill is the click target (clears the filter); it darkens on
// hover so it reads as "click to remove". Pops in/out on a snappy spring. Mount
// inside an <AnimatePresence> so the exit runs.
export const ActiveFilterChip = ({ field, value, onClear, reduced }: { field: string; value: string; onClear: () => void; reduced: boolean }) => (
	<motion.button
		type="button"
		key="filter-chip"
		onClick={onClear}
		aria-label={`Remove ${field} filter`}
		initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.8 }}
		animate={reduced ? { opacity: 1 } : { opacity: 1, scale: 1 }}
		exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.8 }}
		transition={reduced ? { duration: 0.12 } : { type: 'spring', stiffness: 560, damping: 26 }}
		className="group flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg bg-[rgb(var(--cg-selected))] py-1.5 pl-2.5 pr-2 transition-colors hover:bg-[#cfc8fb]"
	>
		<span className="text-[14px] font-medium text-[rgb(var(--cg-text-secondary))]">{field}:</span>
		<span className="text-[14px] font-medium text-[rgb(var(--cg-text-primary))]">{value}</span>
		<XClose className="h-3.5 w-3.5 text-[#8b8a8f] transition-colors group-hover:text-[rgb(var(--cg-text-primary))]" />
	</motion.button>
)

// Re-export so filter areas built from these parts can animate the chip without
// importing framer-motion themselves.
export { AnimatePresence }
